import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/axios';

const PROFILE_PROMPTS = [
  { key: 'simple_pleasures', text: 'My simple pleasures...' },
  { key: 'geek_out', text: 'I geek out on...' },
  { key: 'life_goal', text: 'A life goal of mine...' },
  { key: 'unusual_skill', text: 'My most unusual skill...' },
  { key: 'green_flags', text: 'My green flags...' },
  { key: 'perfect_sunday', text: 'My perfect Sunday...' },
  { key: 'deal_breaker', text: 'My biggest deal breaker...' },
  { key: 'best_travel', text: 'Best travel story...' },
  { key: 'dinner_guest', text: 'Dream dinner guest...' },
  { key: 'hot_take', text: 'My most controversial opinion...' },
  { key: 'love_language', text: 'My love language is...' },
  { key: 'pet_peeve', text: 'My biggest pet peeve...' },
  { key: 'guilty_pleasure', text: 'My guilty pleasure...' },
  { key: 'two_truths_lie', text: 'Two truths and a lie...' },
  { key: 'typical_sunday', text: 'You can find me on a typical Sunday...' },
  { key: 'first_thing_noticed', text: 'First thing people notice about me...' },
  { key: 'never_do_again', text: 'Something I will never do again...' },
  { key: 'most_spontaneous', text: 'Most spontaneous thing I have done...' },
  { key: 'believe_in', text: 'I believe that...' },
  { key: 'change_mind', text: 'Something that changed my mind...' },
];

const styles = {
  counter: {
    fontSize: '13px',
    color: 'var(--text-muted)',
    marginBottom: '16px',
  },
  item: {
    padding: '14px',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--border-color)',
    cursor: 'pointer',
    transition: 'all 200ms',
    marginBottom: '8px',
  },
  itemSelected: {
    borderColor: 'var(--accent)',
    background: 'var(--accent-soft)',
  },
  promptText: {
    fontSize: '14px',
    fontWeight: '500',
  },
  charCount: {
    fontSize: '11px',
    color: 'var(--text-muted)',
    textAlign: 'right',
    marginTop: '4px',
  },
};

export default function PromptSelector() {
  const { user, refreshUser } = useAuth();
  const [selected, setSelected] = useState([]);
  const [responses, setResponses] = useState({});
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (user?.prompts?.length) {
      setSelected(user.prompts.map((p) => p.prompt_key));
      const resp = {};
      for (const p of user.prompts) {
        resp[p.prompt_key] = p.response;
      }
      setResponses(resp);
    }
  }, [user]);

  const togglePrompt = (key) => {
    setSelected((prev) => {
      if (prev.includes(key)) return prev.filter((k) => k !== key);
      if (prev.length >= 3) return prev;
      return [...prev, key];
    });
  };

  const handleSave = async () => {
    if (selected.length < 3) {
      setError('Please select 3 prompts');
      return;
    }
    for (const key of selected) {
      if (!responses[key]?.trim()) {
        setError('Please write a response for each prompt');
        return;
      }
    }

    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await api.put('/users/profile', {
        prompts: selected.map((key, i) => ({
          prompt_key: key,
          response: responses[key],
          position: i,
        })),
      });
      await refreshUser();
      setSuccess('Prompts saved');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <p style={styles.counter}>{selected.length}/3 selected</p>

      {PROFILE_PROMPTS.map((prompt) => {
        const isSelected = selected.includes(prompt.key);
        return (
          <div
            key={prompt.key}
            style={{ ...styles.item, ...(isSelected ? styles.itemSelected : {}) }}
            onClick={() => togglePrompt(prompt.key)}
          >
            <div style={styles.promptText}>{prompt.text}</div>
            {isSelected && (
              <div onClick={(e) => e.stopPropagation()}>
                <textarea
                  value={responses[prompt.key] || ''}
                  onChange={(e) =>
                    setResponses((prev) => ({
                      ...prev,
                      [prompt.key]: e.target.value.slice(0, 300),
                    }))
                  }
                  placeholder="Write your answer..."
                  rows={2}
                  style={{ marginTop: '10px', resize: 'vertical' }}
                  maxLength={300}
                />
                <p style={styles.charCount}>
                  {(responses[prompt.key] || '').length}/300
                </p>
              </div>
            )}
          </div>
        );
      })}

      {error && <p className="error-text" style={{ marginTop: '12px' }}>{error}</p>}
      {success && (
        <p style={{ color: 'var(--success)', fontSize: '13px', marginTop: '12px' }}>
          {success}
        </p>
      )}

      <button
        className="btn btn-primary"
        onClick={handleSave}
        disabled={saving}
        style={{ marginTop: '16px' }}
      >
        {saving ? 'Saving...' : 'Save prompts'}
      </button>
    </div>
  );
}
